import { useQuery } from '@tanstack/react-query'
import api from '../../api'
import { cn } from '../../lib/utils'
import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react'

interface CloseReadinessProps {
  periodCode: string
  entityCode: string
  className?: string
}

/* ── Score colors ─────────────────────────────────────────── */

function gradeColor(score: number): string {
  if (score >= 90) return '#4ADE80'
  if (score >= 75) return '#60A5FA'
  if (score >= 60) return '#FACC15'
  if (score >= 40) return '#FB923C'
  return '#F87171'
}

function barColor(score: number): string {
  if (score >= 80) return '#4ADE80'
  if (score >= 60) return '#FACC15'
  return '#F87171'
}

/* ── Score circle (SVG gauge) ─────────────────────────────── */

function ScoreCircle({ score, grade }: { score: number; grade: string }) {
  const r = 45
  const circumference = 2 * Math.PI * r
  const progress = (Math.min(score, 100) / 100) * circumference
  const color = gradeColor(score)

  return (
    <svg width="110" height="110" viewBox="0 0 100 100" className="shrink-0">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#1E293B" strokeWidth="6" />
      <circle
        cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="6"
        strokeDasharray={`${progress} ${circumference}`}
        strokeLinecap="round"
        transform="rotate(-90 50 50)"
        className="transition-all duration-700 ease-out"
      />
      <text x="50" y="42" textAnchor="middle" fill={color} fontSize="26" fontWeight="bold">
        {grade}
      </text>
      <text x="50" y="62" textAnchor="middle" fill="#94A3B8" fontSize="13">
        {score}/100
      </text>
    </svg>
  )
}

/* ── Component bar ────────────────────────────────────────── */

const COMPONENT_LABELS: Record<string, string> = {
  exception_resolution: 'Exception Resolution',
  pending_approvals: 'Pending Approvals',
  high_severity: 'High Severity Items',
  reconciliation: 'Reconciliation',
  intercompany: 'Intercompany',
}

function ComponentBar({ label, score, weight }: { label: string; score: number; weight: number }) {
  return (
    <div className="mb-2.5">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[12px] text-[var(--text-secondary,#CBD5E1)]">{label}</span>
        <span className="text-[11px] tabular-nums text-[var(--text-muted,#94A3B8)]">
          {Math.round(score)}%
          <span className="ml-1 text-[var(--text-hint,#475569)]">({weight}%)</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-[#1E293B] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{
            width: `${Math.min(score, 100)}%`,
            background: barColor(score),
          }}
        />
      </div>
    </div>
  )
}

/* ── Blocking item ────────────────────────────────────────── */

function BlockingItem({ item }: { item: any }) {
  const isCritical = item.type === 'critical_item' || (item.exposure && item.exposure > 100_000)
  return (
    <div className="flex items-start gap-2 py-1.5">
      {isCritical ? (
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#F87171]" />
      ) : (
        <Clock className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#FACC15]" />
      )}
      <span className="text-[12px] leading-relaxed text-[var(--text-secondary,#CBD5E1)]">
        {item.detail}
      </span>
    </div>
  )
}

/* ── Main widget ──────────────────────────────────────────── */

export function CloseReadiness({ periodCode, entityCode, className }: CloseReadinessProps) {
  const { data: readiness, isLoading } = useQuery({
    queryKey: ['close-readiness', periodCode, entityCode],
    queryFn: () => api.get('/metrics/close-readiness', {
      params: { entity_code: entityCode, period_code: periodCode || undefined },
    }).then(r => r.data),
    staleTime: 60_000,
    enabled: !!periodCode,
  })

  if (isLoading) {
    return (
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-5',
        className,
      )}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-32 rounded bg-white/[0.04]" />
          <div className="flex gap-6">
            <div className="h-[110px] w-[110px] rounded-full bg-white/[0.04]" />
            <div className="flex-1 space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-2 rounded bg-white/[0.04]" />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!readiness) return null

  const score = readiness.score ?? 0
  const grade = readiness.grade ?? 'F'
  const components = readiness.components || {}
  const blocking = readiness.blocking_items || []

  return (
    <div className={cn(
      'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-5',
      className,
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-[var(--accent,var(--accent))]" />
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            Close Readiness
          </span>
        </div>
        <span className="text-[11px] text-[var(--text-hint,#475569)]">
          {readiness.period}
        </span>
      </div>

      {/* Score + Components */}
      <div className="flex gap-6">
        {/* Left: Score circle + summary */}
        <div className="flex flex-col items-center gap-2 shrink-0">
          <ScoreCircle score={score} grade={grade} />
          {readiness.estimated_close_date && (
            <div className="text-center">
              <p className="text-[11px] text-[var(--text-muted,#94A3B8)]">Est. close</p>
              <p className="text-[12px] font-medium text-[var(--text-primary,#F0F2F5)]">
                {new Date(readiness.estimated_close_date).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric',
                })}
              </p>
              {readiness.vs_target && (
                <p className={cn(
                  'text-[10px]',
                  readiness.vs_target.startsWith('+') ? 'text-[#FB923C]' : 'text-[#4ADE80]',
                )}>
                  {readiness.vs_target}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right: Component bars */}
        <div className="flex-1 min-w-0">
          {Object.entries(components).map(([key, comp]: [string, any]) => (
            <ComponentBar
              key={key}
              label={COMPONENT_LABELS[key] || key.replace(/_/g, ' ')}
              score={comp.score}
              weight={comp.weight}
            />
          ))}
        </div>
      </div>

      {/* Blocking items */}
      {blocking.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/[0.06]">
          <p className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] mb-2">
            Blocking Items ({blocking.length})
          </p>
          <div className="space-y-0.5">
            {blocking.map((item: any, i: number) => (
              <BlockingItem key={i} item={item} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
